import React, {Dispatch, useEffect, useRef} from 'react'
import {FormattedMessage} from 'react-intl'
import {StackProps, TextProps, useMultiStyleConfig, VStack, Text, HStack, useDisclosure, Link, Box, BoxProps} from '@chakra-ui/react'
import {Button, ButtonProps, Icon, ShareBox} from '_uiComponents'
import {useViewport} from '_customHooks'
import type {ProductType} from '_types'
import SizeSelector from '../../partials/size-selector'

interface ProductTileOverlayWishlistProps {
    product: ProductType
    productLink: string
    isInStock: boolean
    selectedSize?: string | null
    selectedColor: string
    sizeErrorVisible: boolean
    setSizeErrorVisible: Dispatch<React.SetStateAction<boolean>>
    setSelectedSize: Dispatch<React.SetStateAction<string | null>>
    addProductToCart: () => void
}

/**
 * @name ProductTileOverlayWishlist
 *
 * @component
 *
 * @description
 * Renders an overlay on the product tile for wishlist page product listing grid.
 *
 * @param {ProductTileOverlayWishlistProps} props - Properties object.
 * @param {ProductType} props.product - The product for which overlay is being rendered.
 * @param {string} props.productLink - Product link.
 * @param {boolean} props.isInStock - Indicates if the product is available and in stock.
 * @param {string | null} [props.selectedSize] - Selected size.
 * @param {boolean} props.sizeErrorVisible - If should show error message for size selection.
 * @param {Dispatch<React.SetStateAction<boolean>>} props.setSizeErrorVisible - Function to update error state.
 * @param {string} props.selectedColor - Selected color.
 * @param {Dispatch<React.SetStateAction<string | null>>} props.setSelectedSize - Function to update the selected size.
 * @param {() => void} props.addProductToCart - Function to add to cart.
 *
 * @returns {JSX.Element} The rendered `ProductTileOverlayWishlist` component.
 */
const ProductTileOverlayWishlist: React.FC<ProductTileOverlayWishlistProps> = ({
    product,
    productLink,
    isInStock,
    selectedSize,
    selectedColor,
    sizeErrorVisible,
    setSizeErrorVisible,
    setSelectedSize,
    addProductToCart,
}) => {
    const styles = useMultiStyleConfig('ProductTileOverlay', {variant: 'wishlist'}) as Record<string, BoxProps & StackProps & TextProps>
    const {isMobile} = useViewport()
    const {isOpen: isShareProductPopoverOpen, onToggle: onToggleShareProductPopover, onClose: onCloseShareProductPopover} = useDisclosure()

    const similarProductsHref = product?.c_variantData?.primaryCategoryUrl || product?.c_variationGroupsData?.[0]?.primaryCategoryUrl
    const selectedSizeLabel = product?.c_variantData ? product?.c_variantData?.size?.label : undefined

    const addToCartButton = useRef<HTMLButtonElement | null>(null)

    if (product?.c_variantData?.ID && !selectedSize) {
        setSelectedSize(product?.c_variantData?.ID)
    }

    /**
     * Handles tab navigation for sizes and add to cart button.
     */
    useEffect(() => {
        if (!isMobile) {
            const parentTile = document.querySelector(`[data-testid="product-tile-${product.id}"]`)
            const overlay = parentTile?.querySelector('[data-testid="wishlist-product-overlay"]')

            if (!overlay) return

            const activeElement = document.activeElement
            if (activeElement && !parentTile?.contains(activeElement)) {
                return
            }

            const firstSizeButton = overlay.querySelector('[data-element-type="size-button"]') as HTMLElement
            if (firstSizeButton) {
                firstSizeButton.focus()
            } else if (addToCartButton.current) {
                addToCartButton.current.focus()
            }
        }
    }, [])

    return (
        <VStack align="start" spacing={3} {...styles.wrapper} data-testid="wishlist-product-overlay">
            {!isInStock ? (
                <VStack {...styles.productNotAvailableWrapper}>
                    <Text {...styles.notAvailableLabel}>
                        <FormattedMessage id="wishlist.no.available" defaultMessage="No longer available" />
                    </Text>
                    <Button variant="textLink" size="md" width="md" as={Link} href={similarProductsHref} enableWrap>
                        <FormattedMessage id="go.to.category" defaultMessage={'Find similar products'} />
                    </Button>
                </VStack>
            ) : (
                <>
                    <VStack {...styles.shareContainer}>
                        <ShareBox isOpen={isShareProductPopoverOpen} onClose={onCloseShareProductPopover} shareUrl={productLink}>
                            <HStack as="button" {...styles.shareButton} onClick={onToggleShareProductPopover}>
                                <Text {...styles.shareLabel}>
                                    <FormattedMessage id="label.product.share" defaultMessage={'Condividi'} />
                                </Text>
                                <Icon name="IconShare" size="md" isFilled />
                            </HStack>
                        </ShareBox>

                        <Text {...styles.skuLabel}>
                            <FormattedMessage id="product.item" defaultMessage={'SKU:'} /> {product.id}
                        </Text>
                    </VStack>

                    <VStack {...styles.overlayMainContent}>
                        <Text {...styles.selectedColorLabel}>
                            <FormattedMessage id="product.label.color" defaultMessage={'Color:'} />{' '}
                            <Text as="span" {...styles.selectedColor}>
                                {selectedColor}
                            </Text>
                        </Text>

                        {selectedSizeLabel ? (
                            <Text {...styles.selectedSizeLabel}>
                                <FormattedMessage id="product.label.size" defaultMessage={'Size:'} /> {selectedSizeLabel}
                            </Text>
                        ) : (
                            <SizeSelector
                                product={product}
                                selectedSize={selectedSize}
                                sizeErrorVisible={sizeErrorVisible}
                                setSizeErrorVisible={setSizeErrorVisible}
                                variant="wishlist"
                                setSelectedSize={setSelectedSize}
                            />
                        )}

                        <Box {...styles.addToCartButtonWrapper}>
                            <Button
                                ref={addToCartButton}
                                data-testid="add-to-cart-button"
                                {...(styles.addToCartButton as ButtonProps)}
                                enableWrap
                                size={'md'}
                                onClick={addProductToCart}
                            >
                                <FormattedMessage id="buy.button" defaultMessage="Add to cart" />
                            </Button>

                            {sizeErrorVisible && (
                                <Box {...styles.addToCartNoSizeWrapper}>
                                    <Text as="span" {...styles.addToCartNoSize}>
                                        <FormattedMessage id="error.select.size" defaultMessage="Select the size to proceed" />
                                    </Text>
                                </Box>
                            )}
                        </Box>

                        <Button {...(styles.goToProductButton as ButtonProps)} enableWrap variant="textLink" size="md" width="md" as={Link} href={productLink}>
                            <FormattedMessage id={'go.to.product'} defaultMessage={'Go to product'} />
                        </Button>
                    </VStack>
                </>
            )}
        </VStack>
    )
}

export {ProductTileOverlayWishlist}
